import { Client, ClientFormData } from '../types';
import { CreateClientRequest } from '../types/dto/request/create-client.dto';
import { UpdateClientRequest } from '../types/dto/request/update-client.dto';
import {
  findChangedQuickBooksSyncedFields,
  mapClientToQuickBooksSyncedValues,
  QuickBooksSyncedField,
} from '../utils/quickbooks-synced-fields';
import { mapClientIntegrationsToDTO } from './client-integration.mapper';
import { mapClientPaymentTermsToDTO } from './client-payment-terms.mapper';

/**
 * Maps ClientFormData to CreateClientRequest with avatar
 * Transforms domain model to DTO structure for client creation
 * Includes avatar file for post-creation upload
 */
export const mapClientFormDataToCreateRequest = (
  formData: ClientFormData
): CreateClientRequest & { avatar?: File } => {
  const integrationsDTO = mapClientIntegrationsToDTO(formData.integrations);

  return {
    info: {
      name: formData.name,
      defaultPaymentTerms: mapClientPaymentTermsToDTO(formData.paymentTerms),
      email: formData.email || '',
      managerId: formData.managerId ? Number(formData.managerId) : null,
      billTo: formData.billingAddress || '',
      shipTo: formData.shippingAddress || '',
    },
    integrations: integrationsDTO
      ? {
          integrations: integrationsDTO,
        }
      : undefined,
    avatar: formData.avatar,
  };
};

/**
 * Maps ClientFormData to UpdateClientRequest
 * Sends only the synced fields changed since the client was loaded, so a value saved meanwhile
 * (in QuickBooks or by another user) is not overwritten with the one the form opened with
 */
export const mapClientFormDataToUpdateRequest = (
  formData: ClientFormData,
  client: Client
): UpdateClientRequest => {
  const changedFields = findChangedQuickBooksSyncedFields(
    formData,
    mapClientToQuickBooksSyncedValues(client)
  );
  const isChanged = (field: QuickBooksSyncedField) => changedFields.includes(field);

  return {
    id: Number(client.id),
    managerId: formData.managerId ? Number(formData.managerId) : null,
    ...(isChanged('name') && { name: formData.name }),
    ...(isChanged('paymentTerms') && {
      defaultPaymentTerms: mapClientPaymentTermsToDTO(formData.paymentTerms),
    }),
    ...(isChanged('email') && { email: formData.email || '' }),
    ...(isChanged('billingAddress') && { billTo: formData.billingAddress || '' }),
    ...(isChanged('shippingAddress') && { shipTo: formData.shippingAddress || '' }),
  };
};
